/**
 * LAS UBICACIONES Y SUS CULTIVOS PARA EL ESCÁNER (plan cultivos SIEX F7, 05-10-2026).
 *
 * La foto del escáner no viene de un sector, así que la IA no sabe el cultivo. Con los
 * chips «Ubicación» y «Cultivo» la persona se lo dice con dos toques (o «No lo sé»: la
 * lista sale de todos los cultivos del sitio). Aquí se leen, con la RLS de quien mira y
 * recortado a sus fincas (`useLocationScope`), las ubicaciones y los cultivos ABIERTOS de
 * cada una, con su código SIEX: el del ciclo, el del bloque o el del nombre si es 100 %
 * seguro (`resolverCultivoSeguro`, niveles A/B). Un cultivo sin código («Varios») no sale:
 * sin código no hay lista que pedir.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useLocationScope } from "@/hooks/useLocationScope";
import { resolverCultivoSeguro } from "@/lib/crops/resolver";

export interface ScanLocation {
  id: string;
  name: string;
  /** Códigos SIEX de los cultivos abiertos ahí, sin repetir, con el nombre guardado más usado. */
  crops: Array<{ code: number; name: string }>;
}

interface FilaCiclo { block_id: string | null; crop_code?: number | null; crop_name: string | null }
interface FilaBloque { id: string; greenhouse_id: string; crop_code?: number | null; crop_name: string | null }

const codigoDe = (code: unknown, nombre: string | null | undefined): number | null => {
  const n = Number(code);
  if (code != null && Number.isInteger(n) && n > 0) return n;
  if (!nombre || !nombre.trim()) return null;
  return resolverCultivoSeguro(nombre).code;
};

/** Pura: ubicaciones + naves + bloques + ciclos abiertos → cultivos por ubicación. */
export function cultivosPorUbicacion(
  locations: Array<{ id: string; name: string }>,
  greenhouses: Array<{ id: string; location_id: string | null }>,
  blocks: FilaBloque[],
  cycles: FilaCiclo[],
): ScanLocation[] {
  const locDeNave = new Map(greenhouses.map((g) => [g.id, g.location_id]));
  const locDeBloque = new Map(blocks.map((b) => [b.id, locDeNave.get(b.greenhouse_id) ?? null]));
  const porLoc = new Map<string, Map<number, Map<string, number>>>();
  const sumar = (loc: string | null | undefined, code: number | null, nombre: string | null) => {
    if (!loc || code == null) return;
    const m = porLoc.get(loc) ?? new Map<number, Map<string, number>>();
    const nombres = m.get(code) ?? new Map<string, number>();
    const n = (nombre ?? "").trim();
    if (n) nombres.set(n, (nombres.get(n) ?? 0) + 1);
    m.set(code, nombres);
    porLoc.set(loc, m);
  };
  const conCiclo = new Set<string>();
  for (const c of cycles) {
    if (!c.block_id) continue;
    conCiclo.add(c.block_id);
    sumar(locDeBloque.get(c.block_id), codigoDe(c.crop_code, c.crop_name), c.crop_name);
  }
  // Un bloque sin ciclo abierto aún dice su cultivo (el espejo).
  for (const b of blocks) {
    if (conCiclo.has(b.id)) continue;
    sumar(locDeBloque.get(b.id), codigoDe(b.crop_code, b.crop_name), b.crop_name);
  }
  return locations.map((l) => {
    const m = porLoc.get(l.id) ?? new Map();
    const crops = [...m.entries()]
      .map(([code, nombres]) => ({
        code,
        name: [...(nombres as Map<string, number>).entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? String(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    return { id: l.id, name: l.name, crops };
  });
}

export function useScanCropChoices(enabled: boolean) {
  const { company } = useAuth();
  const { allowedLocationIds, loading: scopeLoading } = useLocationScope();
  const query = useQuery({
    queryKey: ["scan-crop-choices", company?.id ?? null],
    enabled: enabled && !!company?.id,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<ScanLocation[]> => {
      const cid = company!.id;
      const [locs, ghs, blocks, cycles] = await Promise.all([
        supabase.from("locations").select("id, name").eq("company_id", cid).order("name"),
        supabase.from("greenhouses").select("id, location_id").eq("company_id", cid),
        supabase.from("blocks").select("id, greenhouse_id, crop_code, crop_name").eq("company_id", cid).is("archived_at", null),
        supabase.from("crop_cycles").select("block_id, crop_code, crop_name").eq("company_id", cid).is("end_date", null),
      ]);
      if (locs.error) throw locs.error;
      return cultivosPorUbicacion(
        (locs.data ?? []) as Array<{ id: string; name: string }>,
        (ghs.data ?? []) as Array<{ id: string; location_id: string | null }>,
        (blocks.data ?? []) as FilaBloque[],
        (cycles.data ?? []) as FilaCiclo[],
      );
    },
  });
  const todas = query.data ?? [];
  const visibles = allowedLocationIds ? todas.filter((l) => allowedLocationIds.has(l.id)) : todas;
  return { locations: visibles, loading: query.isLoading || scopeLoading };
}
